import React, { useContext, useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { LocalizationContext } from '../LanguageContext/LocalizationContext';
import Validator from "validator";
import { IconButton, InputAdornment, useMediaQuery, Typography } from '@material-ui/core';
import Tooltip from "../CustomStyleComponents/Tooltip";
import HelpOutlineIcon from '@material-ui/icons/HelpOutlineRounded';
import { isTahystysklinikka, isUniklinikka, svIsUniklinikka, svIsTahystysklinikka, enIsUniklinikka, enIsTahystysklinikka, isValidPhoneNr } from '../utils';
import { DefaultButton, SuccessButton } from '../CustomStyleComponents/Buttons';
import { LabelOutSideTextField, StyledTextField } from '../CustomStyleComponents/TextFields';
import { FieldStatusIcon } from '../UtilComponents/FieldStatusIcon';
import { PrivacyPolicyLink } from '../Links';
import { defaultCustomer } from '../../settings';
import { colors } from '../../theme';
import { getPrettyDate, getTime } from "../utils";
import { PopulatedTimeslot } from '../../types';
import memoryStorage from '../../util/memoryStorage';
import strongAuthService from '../../services/strongAuthService';
import SsnMismatchPopup from '../SsnMismatchPopup';

const useStyles = (mobile: boolean) => makeStyles(theme => ({
    form: {
        "& .MuiGrid-item": {
            height: "100%"
        },
        ...(mobile ? {
            "& >.MuiGrid-item:last-child": {
                paddingTop: 0
            }
        } : {})
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
    helpIcon: {
        color: colors.coronariaBlue
    },
    infoIconContainer: {
        display: "flex",
        paddingLeft: theme.spacing(0) + "!important",
        paddingTop: theme.spacing(2) + "!important",
        paddingRight: (mobile ? theme.spacing(0) : theme.spacing(2)) + "!important",
        paddingBottom: theme.spacing(2) + "!important",
        justifyContent: mobile ? "flex-end" : "flex-start",
    },
    labelText: {
        color: "#595959",
        fontSize: theme.typography.pxToRem(16),
        lineHeight: theme.typography.pxToRem(16),
        letterSpacing: theme.typography.pxToRem(0.4),
        backgroundColor: "#FFFFFF",
        paddingRight: theme.typography.pxToRem(4),
        "&::placeholder": {
            fontFamily: "Gotham Narrow Book",
            fontSize: theme.typography.pxToRem(16),
            letterSpacing: theme.typography.pxToRem(0.13),
            lineHeight: theme.typography.pxToRem(24),
            color: "rgba(33,33,33)",
            opacity: 0.7
        }
    },
    textarea: {
        minHeight: "48px"
    },
    blue: {
        color: theme.palette.text.secondary
    },
    noBottomPadding: {
        paddingBottom: "0px"
    }
}));

const checkSumMatches = (hetu: string) => {
    const mark = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "A", "B", "C", "D",
        "E", "F", "H", "J", "K", "L", "M", "N", "P", "R",
        "S", "T", "U", "V", "W", "X", "Y"];

    const calculateCheckCharId = (str: string) =>
        parseInt(str) % 31;

    const calculatesToValidCheckChar = (hetuWithoutCheckChar: string) =>
        Validator.isNumeric(hetuWithoutCheckChar) &&
        calculateCheckCharId(hetuWithoutCheckchar) < mark.length;

    const hetuWithoutCheckchar = hetu.slice(0, 6) + hetu.slice(7, 10);
    const checkChar = hetu.slice(10, 11);

    return calculatesToValidCheckChar(hetuWithoutCheckchar) &&
        mark[parseInt(hetuWithoutCheckchar) % 31] === checkChar;
};

const bookingForOtherAndHetuMatchesTokenSub = (hetu: string): boolean => {
    if (memoryStorage.getItem('bookForOther')) {
        const token = strongAuthService.getTokenPayload();
        if (token) {
            if (token.sub === hetu) {
                return true;
            }
        }
    }
    return false;
};

const isValidHetu = (hetu: string) => {
    if (bookingForOtherAndHetuMatchesTokenSub(hetu)) return false;

    const hetuRegex =
        /^(?!000000-0000)(?:\d{6}[-+A]\d{3}\S{1}|\d{6}[a-zA-Z]\d{3}\S{1})$/;
    return hetuRegex.test(hetu) && checkSumMatches(hetu);
};
const isValidLocality = (str: string) => isOnlyLetters(str)
    && isAtleastOneCharLong(str)
    && isLessThan128CharsLong(str);
const isAtleastOneCharLong = (str: string) => str.trim().length > 0;
const isLessThan128CharsLong = (str: string) => str.trim().length < 128;
const isValidEmail = (email: string) => RegExp(/\S+@\S+\.\S+/).test(email)
    && isLessThan128CharsLong(email);
const isValidName = (name: string) => isAtleastOneCharLong(name) && isLessThan128CharsLong(name);
const isValidStreetAddress = (name: string) => isAtleastOneCharLong(name)
    && isLessThan128CharsLong(name);
const isValidPostalNumber = (postalNr: string) => RegExp(/^([0-9]){5}$/g).test(postalNr);
const isOnlyLetters = (str: string) => RegExp(/^[a-zA-Z-äöåÄÖÅ\s]*$/).test(str);
const isServiceVoucherFilled = (serviceVoucherCode: string, customerType?: string) =>
    (customerType === "palveluseteli" && isAtleastOneCharLong(serviceVoucherCode)) ||
    customerType !== "palveluseteli";

const allValid = (
    hetu: string,
    phoneNr: string,
    email: string,
    firstName: string,
    lastName: string,
    streetAddress: string,
    postalCode: string,
    localityName: string,
    dataConsent: boolean,
    serviceVoucherCode: string,
    customerType?: string
) =>
    isValidHetu(hetu) &&
    isValidPhoneNr(phoneNr) &&
    isValidEmail(email) &&
    isValidName(firstName) &&
    isValidName(lastName) &&
    isValidStreetAddress(streetAddress) &&
    isValidPostalNumber(postalCode) &&
    isValidLocality(localityName) &&
    dataConsent &&
    isServiceVoucherFilled(serviceVoucherCode, customerType);


enum HetuValidationStates {
    NotValidated = "NotValidated",
    Invalid = "Invalid",
    Valid = "Valid",
}

interface InformationFormProps {
    timeslot: PopulatedTimeslot,

    handleNext: () => void,
    handleBack: () => void,
    hetu: string,
    setHetu: (hetu: string) => void,
    phoneNr: string,
    setPhoneNr: (phoneNr: string) => void,
    email: string,
    setEmail: (email: string) => void,
    firstName: string,
    setFirstName: (firstname: string) => void,
    lastName: string,
    setLastName: (lastname: string) => void,

    streetAddress: string,
    setStreetAddress: (str: string) => void,

    postalCode: string,
    setPostalCode: (str: string) => void,

    localityName: string,
    setLocalityName: (str: string) => void,

    dataConsent: boolean,
    setDataConsent: (val: boolean) => void,
    moreInformation: string,
    setMoreInformation: (str: string) => void,
    customerType?: string,
    serviceVoucherCode: string,
    setServiceVoucherCode: (code: string) => void,
    discountCode: string,
    setDiscountCode: (code: string) => void,

    reserverPhoneNumber: string,
    setReserverPhoneNumber: (phoneNumber: string) => void,
    reserverEmail: string,
    setReserverEmail: (email: string) => void,

    authenticated: boolean
}
const InformationForm: React.FC<InformationFormProps> = ({
    timeslot,

    handleNext,
    handleBack,
    hetu,
    setHetu,
    email,
    setEmail,
    phoneNr,
    setPhoneNr,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dataConsent,
    setDataConsent,
    moreInformation,
    setMoreInformation,

    streetAddress,
    setStreetAddress,
    postalCode,
    setPostalCode,
    localityName,
    setLocalityName,
    customerType,
    serviceVoucherCode,
    setServiceVoucherCode,
    discountCode,
    setDiscountCode,

    reserverPhoneNumber,
    setReserverPhoneNumber,
    reserverEmail,
    setReserverEmail,

    authenticated
}) => {
    const theme = useTheme();
    const windowIsMdSize = useMediaQuery(theme.breakpoints.up('md'));

    const classes = useStyles(!windowIsMdSize)();
    const { translation } = useContext(LocalizationContext);
    const [hetuValidationState, setHetuValidationState] =
        useState<HetuValidationStates>(HetuValidationStates.NotValidated);
    const [showSsnMismatchPopup, setShowSsnMismatchPopup] = useState(false);

    let bookForOther = memoryStorage.getItem('bookForOther');

    const tokenPayload = strongAuthService.getTokenPayload();

    useEffect(() => {
        if (!bookForOther && tokenPayload) {
            setHetu(tokenPayload.sub);
            setFirstName(tokenPayload.given_name);
            setLastName(tokenPayload.family_name);
        } else if (bookForOther) {
            if (tokenPayload && hetu === tokenPayload.sub)
                setHetu('');
            if (tokenPayload && firstName === tokenPayload.given_name)
                setFirstName('');
            if (tokenPayload && lastName === tokenPayload.family_name)
                setLastName('');
        }
    }, []);


    const bookingTimeReminder = useMemo(() => (
        <Grid
            item
            xs={12}
            style={{ paddingBottom: "0px" }}
        >
            <Typography
                variant={"body1"}
                component="span"
                style={{marginRight: '5px'}}
            >
                {translation.yourAreAboutToBookTime}
            </Typography>
            <Typography
                variant={"subtitle1"}
                component="span"
                className={classes.blue}
            >
                {
                    `${getPrettyDate(moment.utc(timeslot.start).local(), translation)}` +
                    ` ${translation.clock} ${getTime(timeslot.start)}`
                }
            </Typography>
        </Grid>
    ), [classes]);

    const reserverName = useMemo(() => (
        <Grid
            item
            xs={12}
            style={{ paddingBottom: "0px" }}
        >
        <Typography
            variant={"subtitle1"}
        >
            <b>{translation.reserverName}</b> {tokenPayload?.given_name} {tokenPayload?.family_name}
        </Typography>
        </Grid>
    ), []);

    const reserverInformation = () => {
        if (!bookForOther) return null;

        return (
            <Grid item xs={12}>
                <Grid container spacing={spacing} justify="flex-end">
                    <Grid item xs={12} md={6}>
                        <LabelOutSideTextField
                            value={reserverPhoneNumber}
                            onChange={(e: any) => setReserverPhoneNumber(e.target.value)}
                            variant="outlined"
                            required
                            fullWidth
                            name="reserverPhoneNumber"
                            label={translation.phoneNumber}
                            id="reserverPhoneNumber"
                            autoComplete={"reserver_tel"}
                            error={reserverPhoneNumber !== "" && !isValidPhoneNr(reserverPhoneNumber)}
                            helperText={
                                reserverPhoneNumber !== "" && !isValidPhoneNr(reserverPhoneNumber) ?
                                    translation.phoneError :
                                    ""
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {
                                            <FieldStatusIcon
                                                value={reserverPhoneNumber}
                                                isValid={isValidPhoneNr}
                                            />
                                        }
                                    </InputAdornment>
                                ),
                                classes: { input: classes.labelText }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelOutSideTextField
                            value={reserverEmail}
                            onChange={(e: any) => setReserverEmail(e.target.value)}
                            variant="outlined"
                            required
                            fullWidth
                            name="reserverEmail"
                            label={translation.email}
                            id="reserverEmail"
                            autoComplete={"reserverEmail"}
                            error={reserverEmail !== "" && !isValidEmail(reserverEmail)}
                            helperText={
                                reserverEmail !== "" && !isValidEmail(reserverEmail) ?
                                    translation.emailError :
                                    ""
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {
                                            <FieldStatusIcon
                                                value={reserverEmail}
                                                isValid={isValidEmail}
                                            />
                                        }
                                    </InputAdornment>
                                ),
                                classes: { input: classes.labelText }
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const nextButton = useMemo(() =>
        <Grid item xs={12} md={6}>
            <SuccessButton
                onClick={handleNext}
                fullWidth
                id={"nextButtonConfirmation"}
                disabled={!allValid(
                    hetu,
                    phoneNr,
                    email,
                    firstName,
                    lastName,
                    streetAddress,
                    postalCode,
                    localityName,
                    dataConsent,
                    serviceVoucherCode,
                    customerType
                )}
            >
                {translation.continueToNextPhase}
            </SuccessButton>
        </Grid>
        , [
            hetu, phoneNr, email, firstName, lastName,
            streetAddress, postalCode, localityName, dataConsent, serviceVoucherCode
        ]);

    const backButton = useMemo(() =>
        <Grid item xs={12} md={6}>
            <DefaultButton
                onClick={handleBack}
                fullWidth
            >
                {translation.buttonGoBack}
            </DefaultButton>
        </Grid>
        , []);

    const spacing = 4;

    const validateAndUpdateHetu = (hetu: string) => {
        hetu = hetu.trim().toUpperCase();

        // Check for SSN mismatch first
        if (bookingForOtherAndHetuMatchesTokenSub(hetu)) {
            setShowSsnMismatchPopup(true);
            setHetuValidationState(HetuValidationStates.Invalid);
            setHetu(hetu);
            return;
        }

        if (hetu.length !== 11) {
            setHetuValidationState(HetuValidationStates.NotValidated);
        } else if (isValidHetu(hetu)) {
            setHetuValidationState(HetuValidationStates.Valid);
        } else {
            setHetuValidationState(HetuValidationStates.Invalid);
        }
        setHetu(hetu);
    };

    const handleCloseSsnMismatchPopup = () => {
        setShowSsnMismatchPopup(false);
    };

    if (!authenticated) return (
        <Grid component="form" noValidate container item className={classes.form} spacing={spacing}>
            <Grid
                item
                xs={12}
                style={{ justifyContent: 'center', alignItems: 'center' }}
            >
                <Typography
                    variant={"body1"}
                    className={classes.blue}
                    style={{textAlign: 'center'}}
                >
                    {translation.authenticateBeforeBooking}
                </Typography>
            </Grid>
            {backButton}
        </Grid>
    );

    return (
        <Grid component="form" noValidate container item className={classes.form} spacing={spacing}>
            {reserverName}
            {reserverInformation()}
            {bookingTimeReminder}
            <Grid
                item
                xs={12}
                style={{ paddingBottom: "0px" }}
            >
                <Typography
                    variant={"h6"}
                    className={classes.blue}
                >
                    {bookForOther ? translation.verifySelectionOther : translation.verifySelection}
                </Typography>
            </Grid>
            { bookForOther
                ? <Grid item md={6} xs={10}>
                    <LabelOutSideTextField
                        onChange={(e: any) => validateAndUpdateHetu(e.target.value)}
                        value={hetu}
                        variant="outlined"
                        required
                        fullWidth
                        id="hetu"
                        label={
                            bookForOther
                            ? translation.personalIdentificationNumberOther
                            : translation.personalIdentificationNumber
                        }
                        name="hetu"
                        error={hetuValidationState === HetuValidationStates.Invalid}
                        helperText={
                            hetuValidationState === HetuValidationStates.Invalid ?
                                translation.ssnError :
                                ""
                        }
                        autoComplete={"off"}
                        InputProps={{
                            endAdornment: (
                                hetuValidationState !== HetuValidationStates.NotValidated && <InputAdornment position="end">
                                    {
                                        <FieldStatusIcon
                                            value={hetu}
                                            isValid={() =>
                                                hetuValidationState === HetuValidationStates.Valid
                                            }
                                        />
                                    }
                                </InputAdornment>
                            ),
                            classes: { input: classes.labelText }
                        }}
                    />
                </Grid>
                : null
            }
            { bookForOther ?
                <Grid item xs={2} md={6} className={classes.infoIconContainer}>
                    <Tooltip
                        title={translation.ssnTooltip}
                        arrow={true}
                        interactive
                    >
                        <IconButton
                            size={"medium"}
                            disableRipple={true}
                            style={{ backgroundColor: 'transparent' }}
                        >
                            <HelpOutlineIcon
                                fontSize={"large"}
                                className={classes.helpIcon}
                            />
                        </IconButton>
                    </Tooltip>
                </Grid>
                : null
            }
            {customerType === "palveluseteli" && <Grid item xs={12} md={6}>
                <LabelOutSideTextField
                    value={serviceVoucherCode}
                    onChange={(e: any) => {
                        setServiceVoucherCode(e.target.value);
                    }}
                    variant="outlined"
                    label={translation.serviceVoucherNumber}
                    required
                    fullWidth
                    id="palse"
                    autoComplete="off"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                {
                                    <FieldStatusIcon
                                        value={serviceVoucherCode}
                                        isValid={isAtleastOneCharLong}
                                    />
                                }
                            </InputAdornment>
                        ),
                        classes: { input: classes.labelText }
                    }}
                />

            </Grid>}
            { bookForOther
                ? <Grid item xs={12}>
                    <Grid container spacing={spacing}>
                        <Grid item xs={12} md={6}>
                            <LabelOutSideTextField
                                value={firstName}
                                onChange={(e: any) => setFirstName(e.target.value)}
                                variant="outlined"
                                required
                                fullWidth
                                name="firstname"
                                label={
                                    bookForOther
                                        ? translation.firstNameOther
                                        : translation.firstName
                                }
                                id="firstname"
                                error={firstName !== "" && !isValidName(firstName)}
                                helperText={
                                    firstName !== "" && !isValidName(firstName) ?
                                        translation.lengthError :
                                        ""
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {
                                                <FieldStatusIcon
                                                    value={firstName}
                                                    isValid={isValidName}
                                                />
                                            }
                                        </InputAdornment>
                                    ),
                                    classes: { input: classes.labelText }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelOutSideTextField
                                value={lastName}
                                onChange={(e: any) => setLastName(e.target.value)}
                                variant="outlined"
                                required
                                fullWidth
                                name="lastname"
                                label={
                                    bookForOther
                                    ? translation.lastNameOther
                                    : translation.lastName
                                }
                                id="lastname"
                                error={lastName !== "" && !isValidName(lastName)}
                                helperText={
                                    lastName !== "" && !isValidName(lastName) ?
                                        translation.lengthError :
                                        ""
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {
                                                <FieldStatusIcon
                                                    value={lastName}
                                                    isValid={isValidName}
                                                />
                                            }
                                        </InputAdornment>
                                    ),
                                    classes: { input: classes.labelText }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                : null
            }
            <Grid item container xs={12}>
                <Grid container spacing={spacing}>
                    <Grid item xs={12} md={6}>
                        <LabelOutSideTextField
                            value={streetAddress}
                            onChange={(e: any) => setStreetAddress(e.target.value)}
                            variant="outlined"
                            required
                            fullWidth
                            name="streetAddress"
                            label={
                                bookForOther
                                ? translation.streetAddressOther
                                : translation.streetAddress
                            }
                            id="streetAddress"
                            autoComplete={"shipping street-address"}
                            helperText={
                                streetAddress !== "" && !isValidStreetAddress(streetAddress) ?
                                    translation.lengthError :
                                    ""
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {
                                            <FieldStatusIcon
                                                value={streetAddress}
                                                isValid={isAtleastOneCharLong}
                                            />
                                        }
                                    </InputAdornment>
                                ),
                                classes: { input: classes.labelText }
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={spacing} justify="flex-end">
                    <Grid item xs={12} md={6}>
                        <LabelOutSideTextField
                            value={postalCode}
                            onChange={(e: any) => {
                            const value = e.target.value;

                            if (typeof(value) === 'string') {
                                setPostalCode(value.slice(0, 5));
                            }
                        }}
                            variant="outlined"
                            required
                            fullWidth
                            name="postalCode"
                            label={
                                bookForOther
                                ? translation.postalCodeOther
                                : translation.postalCode
                            }
                            id="postalCode"
                            autoComplete={"shipping postal-code"}
                            error={postalCode !== "" && !isValidPostalNumber(postalCode)}
                            helperText={
                                postalCode !== "" && !isValidPostalNumber(postalCode) ?
                                    translation.postalError :
                                    ""
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {
                                            <FieldStatusIcon
                                                value={postalCode}
                                                isValid={isValidPostalNumber}
                                            />
                                        }
                                    </InputAdornment>
                                ),
                                classes: { input: classes.labelText }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelOutSideTextField
                            value={localityName}
                            onChange={(e: any) => setLocalityName(e.target.value)}
                            variant="outlined"
                            required
                            fullWidth
                            name="locality"
                            label={
                                bookForOther
                                ? translation.districtOther
                                : translation.district
                            }
                            id="locality"
                            autoComplete={"shipping locality"}
                            helperText={
                                localityName !== "" && !isValidLocality(localityName) ?
                                    translation.localityValidationError :
                                    ""
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {
                                            <FieldStatusIcon
                                                value={localityName}
                                                isValid={isValidLocality}
                                            />
                                        }
                                    </InputAdornment>
                                ),
                                classes: { input: classes.labelText }
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={spacing} justify="flex-end">
                    <Grid item xs={12} md={6}>
                        <LabelOutSideTextField
                            value={phoneNr}
                            onChange={(e: any) => setPhoneNr(e.target.value)}
                            variant="outlined"
                            required
                            fullWidth
                            name="phoneNumber"
                            label={
                                bookForOther
                                ? translation.phoneNumberOther
                                : translation.phoneNumber
                            }
                            id="phoneNumber"
                            autoComplete={"tel"}
                            error={phoneNr !== "" && !isValidPhoneNr(phoneNr)}
                            helperText={
                                phoneNr !== "" && !isValidPhoneNr(phoneNr) ?
                                    translation.phoneError :
                                    ""
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {
                                            <FieldStatusIcon
                                                value={phoneNr}
                                                isValid={isValidPhoneNr}
                                            />
                                        }
                                    </InputAdornment>
                                ),
                                classes: { input: classes.labelText }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LabelOutSideTextField
                            value={email}
                            onChange={(e: any) => setEmail(e.target.value)}
                            variant="outlined"
                            required
                            fullWidth
                            name="email"
                            label={
                                bookForOther
                                ? translation.emailOther
                                : translation.email
                            }
                            id="email"
                            autoComplete={"email"}
                            error={email !== "" && !isValidEmail(email)}
                            helperText={
                                email !== "" && !isValidEmail(email) ?
                                    translation.emailError :
                                    ""
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {
                                            <FieldStatusIcon
                                                value={email}
                                                isValid={isValidEmail}
                                            />
                                        }
                                    </InputAdornment>
                                ),
                                classes: { input: classes.labelText }
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {customerType === defaultCustomer && <Grid item xs={12}>
                <LabelOutSideTextField
                    value={discountCode}
                    onChange={(e: any) => {
                        setDiscountCode(e.target.value);
                    }}
                    variant="outlined"
                    label={`${translation.discountCodeNumber} (${translation.discountCodeNumberInfo})`}
                    fullWidth
                    id="discount"
                    autoComplete="off"
                />

            </Grid>}
            <Grid item xs={12}>
                <StyledTextField
                    variant="outlined"
                    value={moreInformation}
                    onChange={(e: any) => setMoreInformation(e.target.value)}
                    fullWidth
                    multiline
                    name="information"
                    label={isTahystysklinikka() || isUniklinikka()
                        || svIsTahystysklinikka() || svIsUniklinikka()
                        || enIsUniklinikka() || enIsTahystysklinikka()
                        ? translation.moreInformationMax300.toUpperCase()
                        : translation.moreInformation.toUpperCase()}
                    placeholder={translation.informationWillOnlyBeAvailableTo}
                    id="information"
                    inputProps={isTahystysklinikka() || isUniklinikka()
                        || svIsTahystysklinikka() || svIsUniklinikka()
                        || enIsUniklinikka() || enIsTahystysklinikka()
                        ? { maxLength: 300 }
                        : {}}
                    InputProps={{
                        classes: { input: `${classes.labelText} ${classes.textarea}` }
                    }}
                    InputLabelProps={{
                        disableAnimation: true,
                        shrink: true,
                    }}
                />
            </Grid>
            <Grid
                item
                xs={12}
                style={{ paddingBottom: "0px" }}
            >
                <Typography
                    variant={"h6"}
                    className={classes.blue}
                >
                    {translation.agreeToDataPrivacyHeader}
                </Typography>
            </Grid>
            <Grid
                item
                xs={12}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={dataConsent}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setDataConsent(event.target.checked)
                            }
                            style={{ pointerEvents: "auto" }}
                            color="primary"
                            required
                        />}
                    label={
                        <Typography>
                            {translation.checkboxDataProtectionAgree}
                            <PrivacyPolicyLink />
                        </Typography>
                    }
                />
            </Grid>
            {
                windowIsMdSize ?
                    backButton :
                    nextButton
            }
            {
                windowIsMdSize ?
                    nextButton :
                    backButton
            }
            <SsnMismatchPopup
                open={showSsnMismatchPopup}
                onClose={handleCloseSsnMismatchPopup}
            />
        </Grid>
    );
};

export default InformationForm;
